import { Flex, Spin } from 'antd';
import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { resolveHomeDestination, useListMembershipsQuery } from '@/entities/user';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { ROUTES } from '@/shared/config/routes';
import { currentStoreChanged } from '@/shared/lib/redux/actions';
import { QueryErrorAlert } from '@/shared/ui';

/** `/` — sends the signed-in user on by their memberships (see `resolveHomeDestination`): none →
 *  /onboarding; an APPROVED one (the persisted selection if still valid, else the first) → its
 *  zone (OWNER → /admin, CLIENT → /shop); only pending/rejected/blocked → /connections. Selects
 *  the chosen store first so the zone's `RequireMembership` sees it. Not wrapped in
 *  `RequireAuth` (it has to decide *where* to send an authenticated user, including away from
 *  `/`), so it must check `auth.status` itself before querying memberships — skipping that check
 *  once caused an infinite loop for a signed-out visit: the query 401s, the failed reauth's
 *  refresh attempt clears the session, whose listener resets the RTK Query cache, which re-fires
 *  this same query — see baseApi.ts's retry fix for the other half of that bug. */
export function RootRedirect() {
  const dispatch = useAppDispatch();
  const status = useAppSelector((state) => state.auth.status);
  const currentStoreId = useAppSelector((state) => state.store.currentStoreId);
  const {
    data: memberships,
    error,
    isLoading,
    refetch,
  } = useListMembershipsQuery(undefined, {
    skip: status !== 'authenticated',
  });

  const destination = memberships ? resolveHomeDestination(memberships, currentStoreId) : null;
  const storeToSelect =
    destination?.storeId && destination.storeId !== currentStoreId ? destination.storeId : null;

  useEffect(() => {
    if (storeToSelect) dispatch(currentStoreChanged(storeToSelect));
  }, [dispatch, storeToSelect]);

  if (status === 'unauthenticated') {
    return <Navigate to={ROUTES.login} replace />;
  }

  if (status === 'authenticated' && !memberships && error) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: '100vh', padding: 16 }}>
        <QueryErrorAlert error={error} onRetry={() => void refetch()} />
      </Flex>
    );
  }

  if (status === 'idle' || isLoading || !destination || storeToSelect) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: '100vh' }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return <Navigate to={destination.to} replace />;
}
